import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "@/components/Header";
import AppSidebar from "@/components/Sidebar";
import { usePostThemes } from "@/context/PostThemesContext";
import { useWordPress } from "@/context/WordPressContext";
import DashboardCalendar from "@/components/DashboardCalendar";
import DashboardRecommendations from "@/components/DashboardRecommendations";
import { IntelligenceAgentCard } from "@/components/IntelligenceAgentCard";
import { useWebsites } from "@/context/WebsitesContext";
import SecondStageOnboardingModal from "@/components/SecondStageOnboardingModal";
import useSecondStageOnboarding from "@/hooks/useSecondStageOnboarding";
import { useAppState } from "@/context/AppStateContext";
import { toast } from "sonner";

const Dashboard = () => {
  const { postThemes, isLoading: isLoadingPostThemes } = usePostThemes();
  const { settings: wpSettings, isLoading: isLoadingWP } = useWordPress();
  const { currentWebsite } = useWebsites();
  const [recentContent, setRecentContent] = useState<any[]>([]);
  const { setCurrentRightPaneUrl } = useAppState();

  // Second stage onboarding
  const { showModal, handleComplete, handleClose, setShowModal } =
    useSecondStageOnboarding() as any;

  // Log component rendering
  console.log("Rendering Dashboard page");

  // Handle pending post creation from onboarding
  useEffect(() => {
    const checkPendingPost = async () => {
      const pendingPostStr = localStorage.getItem('pendingPostCreation');
      if (!pendingPostStr || !currentWebsite?.id) return;

      try {
        const pendingPost = JSON.parse(pendingPostStr);
        console.log('Dashboard: Found pending post creation', pendingPost);
        
        // Clear it immediately to prevent re-creation
        localStorage.removeItem('pendingPostCreation');

        // Create the post_theme record
        const itemId = crypto.randomUUID();
        const { error: insertError } = await supabase
          .from('post_themes')
          .insert({
            id: itemId,
            subject_matter: pendingPost.title,
            post_digest: pendingPost.digest,
            website_id: currentWebsite.id,
            keywords: [],
            categories: []
          });

        if (insertError) {
          console.error('Dashboard: Failed to create post_theme', insertError);
          toast.error('Failed to create post');
          return;
        }

        // Set default section layout
        const excluded = new Set(['quote', 'image', 'testimonial']);
        const DEFAULT_SECTION_TYPES = [
          { id: 'introduction' },
          { id: 'how_to' },
          { id: 'tips' },
          { id: 'benefits' },
          { id: 'conclusion' }
        ];
        const section_layout = DEFAULT_SECTION_TYPES
          .map((s: any) => s.id)
          .filter((id: string) => !excluded.has(id));
        
        if (section_layout.length > 0) {
          await supabase
            .from('post_themes')
            .update({ section_layout })
            .eq('id', itemId);
        }

        // Navigate to ContentEditor
        console.log('Dashboard: Navigating to ContentEditor', { itemId });
        setCurrentRightPaneUrl(`/content-editor/${itemId}`);
        toast.success('Creating your post...');
      } catch (error) {
        console.error('Dashboard: Error processing pending post', error);
        localStorage.removeItem('pendingPostCreation');
      }
    };

    checkPendingPost();
  }, [currentWebsite?.id, setCurrentRightPaneUrl]);

  useEffect(() => {
    const onOpenSecondStage = (e: any) => {
      // If a detail.initialStep is provided, ensure the modal uses it
      if (e?.detail?.initialStep === 2 && !wpSettings?.is_connected) {
        // If not connected yet, force step 1 regardless
        setShowModal(true);
      } else {
        setShowModal(true);
      }
    };
    window.addEventListener("open-second-stage-onboarding" as any, onOpenSecondStage);
    return () =>
      window.removeEventListener(
        "open-second-stage-onboarding" as any,
        onOpenSecondStage
      );
  }, [setShowModal, wpSettings?.is_connected]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("show_second_stage") === "1") {
      setShowModal(true);
    }
  }, [setShowModal]);

  useEffect(() => {
    // Show upcoming content from the calendar
    if (postThemes && postThemes.length > 0) {
      // Filter for upcoming scheduled content
      const now = new Date();
      const upcomingContent = [...postThemes]
        .filter((theme) => {
          try {
            const scheduleDate = new Date(theme.scheduled_date);
            return scheduleDate >= now;
          } catch (e) {
            return false;
          }
        })
        .sort(
          (a, b) =>
            new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime()
        )
        .slice(0, 2)
        .map((theme) => ({
          title: theme.subject_matter,
          description: theme.post_content || "",
          dateCreated: new Date(theme.scheduled_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          contentStatus: theme.status === "published" ? "published" : "scheduled",
          keywords: theme.keywords.map((k) => ({ text: k })),
        }));

      // If no upcoming content, use the most recent
      if (upcomingContent.length === 0) {
        const recentContent = [...postThemes]
          .sort(
            (a, b) =>
              new Date(b.scheduled_date).getTime() - new Date(a.scheduled_date).getTime()
          )
          .slice(0, 2)
          .map((theme) => ({
            title: theme.subject_matter,
            description: theme.post_content || "",
            dateCreated: new Date(theme.scheduled_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            contentStatus: theme.status,
            keywords: theme.keywords.map((k) => ({ text: k })),
          }));

        setRecentContent(recentContent);
      } else {
        setRecentContent(upcomingContent);
      }
    } else if (
      wpSettings &&
      wpSettings.is_connected &&
      currentWebsite?.cms === "wordpress"
    ) {
      setRecentContent([
        {
          title: "WordPress Integration Active",
          description: "Your WordPress site is connected and ready to receive content.",
          dateCreated: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          contentStatus: "published",
          keywords: [{ text: "wordpress" }],
        },
      ]);
    } else {
      // Default state - no planned content or WordPress integration
      setRecentContent([
        {
          title: "Get Started with Planned Content",
          description:
            "Create your first content piece by visiting the Planned Content page.",
          dateCreated: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          contentStatus: "draft",
          keywords: [{ text: "calendar" }],
        },
      ]);
    }
  }, [postThemes, wpSettings, currentWebsite?.cms]);

  // Expose WP connected flag globally for sidebar guard styling/actions
  useEffect(() => {
    (window as any).wpSettingsConnected = Boolean(wpSettings?.is_connected);
  }, [wpSettings?.is_connected]);

  // Background: ensure website has offering/goal when dashboard loads
  useEffect(() => {
    const runBackgroundExtraction = async () => {
      try {
        if (!currentWebsite?.id) return;

        // Fetch current website offering/goal
        const { data: websiteRow, error: websiteErr } = await supabase
          .from("websites")
          .select("id, url, offering, goal")
          .eq("id", currentWebsite.id)
          .single();

        if (websiteErr) {
          console.warn("Dashboard: Failed to read website row", websiteErr);
          return;
        }

        // If already present, nothing to do
        if (websiteRow?.offering && websiteRow?.goal) return;

        // Load existing content to power extraction (non-scraping)
        const { data: contentRows, error: contentErr } = await supabase
          .from("website_content")
          .select("title, content, digest")
          .eq("website_id", currentWebsite.id)
          .limit(500);

        if (contentErr) {
          console.warn("Dashboard: Failed to load website_content", contentErr);
          return;
        }

        if (!contentRows || contentRows.length === 0) {
          // No content available yet; skip silently
          return;
        }

        console.log("Dashboard: Triggering background offering/goal extraction");
        await supabase.functions.invoke("extract-offering-goal", {
          body: {
            website_id: currentWebsite.id,
            website_url: websiteRow?.url || currentWebsite.url || "",
            scraped_content: contentRows.map((c: any) => ({
              title: c.title,
              content: c.content,
              digest: c.digest,
            })),
          },
        });
        // Function persists to DB; UI will reflect on next reads
      } catch (e) {
        console.error("Dashboard: Background goal extraction failed", e);
      }
    };

    runBackgroundExtraction();
  }, [currentWebsite?.id]);

  return (
    <>
      <div className="h-full flex w-full bg-background @container">
        <div className="flex-1 flex flex-col">
          <main className="flex-1 lg:px-4 lg:py-6 overflow-y-auto">
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="grid grid-cols-1 @xl:grid-cols-2 gap-6">
                <div className="@xl:col-span-2">
                  <DashboardCalendar />
                </div>

                <div className="@xl:col-span-2">
                  <DashboardRecommendations />
                </div>
              </div>

              <div className="grid md:grid-cols-1 gap-6">
                <IntelligenceAgentCard />
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Second Stage Onboarding Modal */}
      <SecondStageOnboardingModal
        isOpen={showModal}
        onClose={handleClose}
        onComplete={handleComplete}
        initialStep={wpSettings?.is_connected ? 2 : 1}
      />
    </>
  );
};

export default Dashboard;
